import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Phone, CheckCircle2, Snowflake } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", zip_code: "", address: "", service_requested: "", preferred_timing: "", message: "", website: "" };

export default function LeadFormPage() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    publicApi.get(`/public/lead-form/${code}`)
      .then((r) => setCompany(r.data.company))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please enter your name and phone number.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await publicApi.post(`/public/leads/${code}`, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8] px-6">
        <p className="text-[#71717A]" data-testid="lead-form-not-found">This link is no longer valid.</p>
      </div>
    );
  }

  const mainPhone = company?.phones?.[0];

  return (
    <div className="min-h-screen bg-[#F7F7F8] px-4 py-8" data-testid="public-lead-form-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {company?.logo_url ? (
            <img src={company.logo_url} alt={company.name} className="h-10 w-10 rounded-sm object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-sm bg-[#0055FF] grid place-items-center">
              <Snowflake className="h-5 w-5 text-white" />
            </div>
          )}
          <div>
            <p className="font-heading font-bold text-[#09090B]" data-testid="lead-form-company-name">{company?.name}</p>
            {mainPhone && (
              <a href={`tel:${mainPhone}`} className="text-xs text-[#0055FF] flex items-center gap-1" data-testid="lead-form-company-phone">
                <Phone className="h-3 w-3" /> {mainPhone}
              </a>
            )}
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-md border border-[#E4E4E7] p-8 text-center" data-testid="lead-form-success">
            <CheckCircle2 className="h-10 w-10 text-[#10B981] mx-auto" />
            <h2 className="font-heading text-xl font-bold mt-4 text-[#09090B]">Thank you!</h2>
            <p className="text-sm text-[#71717A] mt-2">
              We got your request and will be in touch shortly. Need it faster?
            </p>
            {mainPhone && (
              <a href={`tel:${mainPhone}`} data-testid="lead-form-success-phone">
                <Button className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
                  <Phone className="h-4 w-4 mr-2" /> Call {mainPhone}
                </Button>
              </a>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-md border border-[#E4E4E7] p-6 space-y-4">
            <h1 className="font-heading text-xl font-bold text-[#09090B]">Get a free quote</h1>
            {error && <p className="text-sm text-[#EF4444]" data-testid="lead-form-error">{error}</p>}

            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Full name *</Label>
              <Input data-testid="lead-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Phone *</Label>
              <Input data-testid="lead-form-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Email</Label>
              <Input data-testid="lead-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Zip code</Label>
                <Input data-testid="lead-form-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Address</Label>
                <Input data-testid="lead-form-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
              </div>
            </div>
            {(company?.services || []).length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Service needed</Label>
                <Select value={form.service_requested} onValueChange={(v) => set("service_requested", v)}>
                  <SelectTrigger data-testid="lead-form-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {company.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">When would you like this done?</Label>
              <Input data-testid="lead-form-timing" placeholder="e.g. this week, next month" value={form.preferred_timing} onChange={(e) => set("preferred_timing", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#71717A]">Anything else we should know?</Label>
              <Textarea data-testid="lead-form-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} />
            </div>
            {/* Honeypot: oculto para humanos, los bots suelen completar cualquier campo visible al DOM */}
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)}
              className="absolute -left-[9999px]" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <Button type="submit" disabled={submitting} className="w-full bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="lead-form-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Request my free quote
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
